/**
 * FO-096 (batch-21) — Verantwoordelijkheden-overzicht.
 *
 * Gedeeld request-schema + read-DTO's (CLAUDE.md: één bron voor API + form,
 * inline veldfouten). Het overzicht legt registratief vast wie (rol óf persoon)
 * waarvoor verantwoordelijk is, los van de technische rechten. ADMIN-only beheer
 * via de AUTORISATIE-permissie; HR/MANAGER read-only inzage.
 *
 * Bewust GEEN `@prisma/client`-import: @streep/types blijft framework-vrij. De
 * `module`-string wordt server-side tegen de Prisma `ModuleType`-enum gevalideerd.
 */

import { z } from 'zod'

// ─── Constanten ────────────────────────────────────────────────────────────────

export const VERANTWOORDELIJKHEIDS_SOORTEN = ['PRIMAIR', 'VERVANGER'] as const
export type VerantwoordelijkheidsSoort = (typeof VERANTWOORDELIJKHEIDS_SOORTEN)[number]

export const VERANTWOORDELIJKHEIDS_SOORT_LABELS: Record<VerantwoordelijkheidsSoort, string> = {
  PRIMAIR: 'Primair verantwoordelijke',
  VERVANGER: 'Vervanger / back-up',
}

/** 1:1 met de Prisma `UserRole`-enum (een gebied kan aan een rol hangen). */
export const VERANTWOORDELIJKHEIDS_ROLLEN = [
  'MONTEUR',
  'TEAMLEIDER',
  'PLANNER',
  'HR',
  'SALARIS',
  'MANAGER',
  'ADMIN',
] as const
export type VerantwoordelijkheidsRol = (typeof VERANTWOORDELIJKHEIDS_ROLLEN)[number]

// ─── Request-schema's ────────────────────────────────────────────────────────

export const createVerantwoordelijkheidsgebiedSchema = z.object({
  naam: z.string().trim().min(2, 'Naam is verplicht (min. 2 tekens)').max(160),
  omschrijving: z.string().trim().max(2000).nullable().optional(),
  /** Optionele module-scope; server-side tegen `ModuleType` gevalideerd. */
  module: z.string().trim().min(1).nullable().optional(),
  /** Optionele BV-scope (Entity-id). */
  bvId: z.string().trim().min(1).nullable().optional(),
})
export type CreateVerantwoordelijkheidsgebiedInput = z.infer<
  typeof createVerantwoordelijkheidsgebiedSchema
>

export const updateVerantwoordelijkheidsgebiedSchema =
  createVerantwoordelijkheidsgebiedSchema.partial()
export type UpdateVerantwoordelijkheidsgebiedInput = z.infer<
  typeof updateVerantwoordelijkheidsgebiedSchema
>

/**
 * Toewijzing: precies één van `rol`/`employeeId` is gevuld (XOR). Spiegelt de
 * DB-check-constraint zodat het form niets kan sturen dat de API/DB weigert.
 */
export const createVerantwoordelijkheidsToewijzingSchema = z
  .object({
    soort: z.enum(VERANTWOORDELIJKHEIDS_SOORTEN).default('PRIMAIR'),
    rol: z.enum(VERANTWOORDELIJKHEIDS_ROLLEN).nullable().optional(),
    employeeId: z.string().trim().min(1).nullable().optional(),
  })
  .refine((v) => (v.rol != null) !== (v.employeeId != null), {
    message: 'Kies precies één van rol of medewerker',
    path: ['rol'],
  })
export type CreateVerantwoordelijkheidsToewijzingInput = z.infer<
  typeof createVerantwoordelijkheidsToewijzingSchema
>

// ─── Read-DTO's ────────────────────────────────────────────────────────────────

export interface VerantwoordelijkheidsToewijzingDTO {
  id: string
  soort: VerantwoordelijkheidsSoort
  /** Gevuld bij een rol-toewijzing (anders null). */
  rol: VerantwoordelijkheidsRol | null
  /** Gevuld bij een persoon-toewijzing (anders null). */
  employeeId: string | null
  employeeNaam: string | null
  /**
   * Recht-mismatch-signaal (FO-096 §6, niet-blokkerend): heeft de
   * verantwoordelijke het bij het gebied gekoppelde module-recht (`canView`)?
   * `null` = geen module gekoppeld, of niet te bepalen (rol-toewijzing).
   */
  heeftModuleRecht: boolean | null
  createdAt: string
}

export interface VerantwoordelijkheidsgebiedDTO {
  id: string
  naam: string
  omschrijving: string | null
  module: string | null
  bvId: string | null
  bvNaam: string | null
  toewijzingen: VerantwoordelijkheidsToewijzingDTO[]
  createdAt: string
  updatedAt: string
}

/** Eén rij per (gebied × persoon) voor de "per persoon"-overdracht-weergave. */
export interface VerantwoordelijkheidPerPersoonDTO {
  employeeId: string
  employeeNaam: string
  gebieden: Array<{
    gebiedId: string
    gebiedNaam: string
    soort: VerantwoordelijkheidsSoort
    module: string | null
    heeftModuleRecht: boolean | null
  }>
}

export interface VerantwoordelijkhedenOverzichtDTO {
  gebieden: VerantwoordelijkheidsgebiedDTO[]
  perPersoon: VerantwoordelijkheidPerPersoonDTO[]
}
